import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';
import {defineMessages, injectIntl} from 'react-intl';
import VM from '@scratch/scratch-vm';

import HardwareDevicePaneComponent from '../components/hardware-device-pane/hardware-device-pane.jsx';
import {
    appendHardwareDevice,
    getAvailableHardwareDevices,
    getCompatibleConnections,
    getDefaultConnectionType,
    getHardwareDevice
} from '../lib/hardware-devices.js';
import {setConnectionModalExtensionId} from '../reducers/connection-modal.js';
import {
    openConnectionModal,
    openDomiDeviceCenter,
    openExtensionLibrary
} from '../reducers/modals.js';
import intlShape from '../lib/intlShape.js';

const ADDRESS_STORAGE_KEY = 'domiCar.address';
const ROUND_ADDRESS_STORAGE_KEY = 'domiAiRound.address';
const DEFAULT_CAR_ADDRESS = '192.168.3.42';
const CAR_CONNECTION_CHANGED_EVENT = 'DOMI_CAR_CONNECTION_CHANGED';
const ROUND_ADDRESS_CHANGED_EVENT = 'DOMI_AI_ROUND_ADDRESS_CHANGED';
const ROUND_CONNECTION_CHANGED_EVENT = 'DOMI_AI_ROUND_CONNECTION_CHANGED';
const OPEN_BURN_CENTER_EVENT = 'OPEN_BURN_CENTER';

const messages = defineMessages({
    connectFailed: {
        id: 'gui.hardwareDevicePane.connectFailed',
        defaultMessage: '连接失败：{message}',
        description: 'Unexpected Domi Car connection failure'
    },
    connectUnavailable: {
        id: 'gui.hardwareDevicePane.connectUnavailable',
        defaultMessage: 'Domi 小车连接服务不可用。',
        description: 'Message when the Domi Car extension connection service is unavailable'
    },
    notReachable: {
        id: 'gui.hardwareDevicePane.notReachable',
        defaultMessage: '没有连接到小车，请检查地址和网络。',
        description: 'Message when the Domi Car cannot be reached over Wi-Fi'
    },
    autoReconnectFailed: {
        id: 'gui.hardwareDevicePane.autoReconnectFailed',
        defaultMessage: '未检测到上次连接的小车，请确认小车电源和网络后重试。',
        description: 'Non-blocking message when automatic Domi Car reconnection fails'
    },
    autoReconnectSuccess: {
        id: 'gui.hardwareDevicePane.autoReconnectSuccess',
        defaultMessage: '已自动连接上次使用的小车。',
        description: 'Message after automatic Domi Car reconnection succeeds'
    },
    connectionLost: {
        id: 'gui.hardwareDevicePane.connectionLost',
        defaultMessage: '小车连接已断开，请重新连接。',
        description: 'Message when a connected Domi Car stops responding'
    },
    roundAutoReconnectFailed: {
        id: 'gui.hardwareDevicePane.roundAutoReconnectFailed',
        defaultMessage: '未检测到上次连接的圆屏，请确认圆屏和电脑处于同一 Wi-Fi。',
        description: 'Non-blocking message when automatic Domi AI round screen reconnection fails'
    },
    roundAutoReconnectSuccess: {
        id: 'gui.hardwareDevicePane.roundAutoReconnectSuccess',
        defaultMessage: '已自动连接上次使用的圆屏。',
        description: 'Message after automatic Domi AI round screen reconnection succeeds'
    },
    roundConnectionLost: {
        id: 'gui.hardwareDevicePane.roundConnectionLost',
        defaultMessage: '圆屏连接已断开，请重新连接。',
        description: 'Message when a connected Domi AI round screen stops responding'
    },
    roundNotReachable: {
        id: 'gui.hardwareDevicePane.roundNotReachable',
        defaultMessage: '没有连接到圆屏，请检查 IP 地址和 Wi-Fi。',
        description: 'Message when the Domi AI round screen cannot be reached'
    },
    roundUnavailable: {
        id: 'gui.hardwareDevicePane.roundUnavailable',
        defaultMessage: 'Domi AI 圆屏连接服务不可用。',
        description: 'Message when the Domi AI round screen extension is unavailable'
    }
});

const loadSavedAddress = deviceId => {
    try {
        const key = deviceId === 'domiAiRound' ? ROUND_ADDRESS_STORAGE_KEY : ADDRESS_STORAGE_KEY;
        return window.localStorage.getItem(key) || '';
    } catch (error) {
        return '';
    }
};

const getDefaultAddress = deviceId => (deviceId === 'domiCar' ? DEFAULT_CAR_ADDRESS : '');

class HardwareDevicePane extends React.Component {
    constructor (props) {
        super(props);
        const devices = getAvailableHardwareDevices(props.vm.extensionManager);
        const selectedDeviceId = devices.length ? devices[0].id : null;
        const selectedDevice = getHardwareDevice(selectedDeviceId);
        const savedAddress = loadSavedAddress(selectedDeviceId);

        bindAll(this, [
            'handleAddressChange',
            'handleAutoConnect',
            'handleCarAddressChanged',
            'handleCarConnectionChanged',
            'handleConnectionStateChange',
            'handleConnectionTypeChange',
            'handleExtensionAdded',
            'handleModeChange',
            'handlePrimaryAction',
            'handleRoundAddressChanged',
            'handleRoundConnectionChanged',
            'handleSelectDevice'
        ]);
        this.state = {
            address: savedAddress || getDefaultAddress(selectedDeviceId),
            connected: this.isDeviceConnected(selectedDeviceId),
            connecting: false,
            connectionType: getDefaultConnectionType(selectedDevice, 'online'),
            deviceInfo: null,
            devices,
            hasSavedAddress: Boolean(savedAddress),
            mode: 'online',
            selectedDeviceId,
            statusMessage: ''
        };
    }
    componentDidMount () {
        this._isMounted = true;
        this.props.vm.on('EXTENSION_ADDED', this.handleExtensionAdded);
        this.props.vm.on('PERIPHERAL_CONNECTED', this.handleConnectionStateChange);
        this.props.vm.on('PERIPHERAL_DISCONNECTED', this.handleConnectionStateChange);
        this.props.vm.runtime.on('DOMI_CAR_ADDRESS_CHANGED', this.handleCarAddressChanged);
        this.props.vm.runtime.on(CAR_CONNECTION_CHANGED_EVENT, this.handleCarConnectionChanged);
        this.props.vm.runtime.on(ROUND_ADDRESS_CHANGED_EVENT, this.handleRoundAddressChanged);
        this.props.vm.runtime.on(ROUND_CONNECTION_CHANGED_EVENT, this.handleRoundConnectionChanged);
        this.handleAutoConnect();
    }
    componentWillUnmount () {
        this._isMounted = false;
        this.props.vm.removeListener('EXTENSION_ADDED', this.handleExtensionAdded);
        this.props.vm.removeListener('PERIPHERAL_CONNECTED', this.handleConnectionStateChange);
        this.props.vm.removeListener('PERIPHERAL_DISCONNECTED', this.handleConnectionStateChange);
        this.props.vm.runtime.removeListener('DOMI_CAR_ADDRESS_CHANGED', this.handleCarAddressChanged);
        this.props.vm.runtime.removeListener(CAR_CONNECTION_CHANGED_EVENT, this.handleCarConnectionChanged);
        this.props.vm.runtime.removeListener(ROUND_ADDRESS_CHANGED_EVENT, this.handleRoundAddressChanged);
        this.props.vm.runtime.removeListener(ROUND_CONNECTION_CHANGED_EVENT, this.handleRoundConnectionChanged);
    }
    isDeviceConnected (deviceId) {
        if (deviceId === 'microbit') return this.props.vm.getPeripheralIsConnected('microbit');
        if (deviceId === 'domiCar') {
            const isConnected = this.props.vm.runtime.getOpcodeFunction('domiCar_isConnected');
            return isConnected ? Boolean(isConnected({}, null)) : false;
        }
        if (deviceId === 'domiAiRound') {
            const isConnected = this.props.vm.runtime.getOpcodeFunction('domiAiRound_isConnected');
            return isConnected ? Boolean(isConnected({}, null)) : false;
        }
        return false;
    }
    handleAddressChange (event) {
        this.setState({
            address: event.target.value,
            connected: false,
            deviceInfo: null,
            hasSavedAddress: false,
            statusMessage: ''
        });
    }
    handleCarAddressChanged (address) {
        if (this.state.selectedDeviceId !== 'domiCar') return;
        this.setState({address, hasSavedAddress: Boolean(address)});
    }
    handleCarConnectionChanged ({connected, deviceInfo} = {}) {
        if (!this._isMounted || this.state.selectedDeviceId !== 'domiCar') return;
        this.setState(previousState => ({
            connected: Boolean(connected),
            connecting: false,
            deviceInfo: connected ? (deviceInfo || previousState.deviceInfo) : null,
            statusMessage: !connected && previousState.connected ?
                this.props.intl.formatMessage(messages.connectionLost) : previousState.statusMessage
        }));
    }
    handleAutoConnect () {
        const {address, connectionType, hasSavedAddress, selectedDeviceId} = this.state;
        if (!hasSavedAddress || connectionType !== 'wifi' || !address.trim()) return;
        if (selectedDeviceId === 'domiCar') this.connectCar(true);
        if (selectedDeviceId === 'domiAiRound') this.connectRound(true, 'wifi');
    }
    handleRoundAddressChanged (address) {
        if (this.state.selectedDeviceId !== 'domiAiRound') return;
        this.setState({address, hasSavedAddress: Boolean(address)});
    }
    handleRoundConnectionChanged ({connected, connectionType, deviceInfo} = {}) {
        if (!this._isMounted || this.state.selectedDeviceId !== 'domiAiRound') return;
        this.setState(previousState => ({
            connected: Boolean(connected),
            connecting: false,
            connectionType: connected && connectionType ? connectionType : previousState.connectionType,
            deviceInfo: connected ? (deviceInfo || previousState.deviceInfo) : null,
            statusMessage: !connected && previousState.connected ?
                this.props.intl.formatMessage(messages.roundConnectionLost) : previousState.statusMessage
        }));
    }
    handleConnectionStateChange () {
        this.setState(previousState => ({
            connected: this.isDeviceConnected(previousState.selectedDeviceId),
            connecting: false,
            statusMessage: ''
        }));
    }
    handleConnectionTypeChange (event) {
        this.setState({
            connected: false,
            connectionType: event.currentTarget.value,
            deviceInfo: null,
            statusMessage: ''
        });
    }
    handleExtensionAdded (categoryInfo) {
        this.setState(previousState => {
            const devices = appendHardwareDevice(previousState.devices, categoryInfo.id);
            const selectedDeviceId = devices.some(device => device.id === previousState.selectedDeviceId) ?
                previousState.selectedDeviceId : (devices.length ? devices[0].id : null);
            const selectedDevice = getHardwareDevice(selectedDeviceId);
            const compatibleConnections = getCompatibleConnections(selectedDevice, previousState.mode);
            const connectionType = compatibleConnections.some(connection => (
                connection.id === previousState.connectionType
            )) ? previousState.connectionType : getDefaultConnectionType(selectedDevice, previousState.mode);
            const savedAddress = loadSavedAddress(selectedDeviceId);
            return {
                address: selectedDeviceId === previousState.selectedDeviceId ? previousState.address :
                    (savedAddress || getDefaultAddress(selectedDeviceId)),
                connected: this.isDeviceConnected(selectedDeviceId),
                connectionType,
                deviceInfo: (selectedDeviceId === 'domiCar' || selectedDeviceId === 'domiAiRound') ?
                    previousState.deviceInfo : null,
                devices,
                hasSavedAddress: selectedDeviceId === previousState.selectedDeviceId ?
                    previousState.hasSavedAddress : Boolean(savedAddress),
                selectedDeviceId
            };
        }, () => {
            if (categoryInfo.id === 'domiCar' || categoryInfo.id === 'domiAiRound') this.handleAutoConnect();
        });
    }
    handleModeChange (event) {
        const mode = event.currentTarget.value;
        const selectedDevice = getHardwareDevice(this.state.selectedDeviceId);
        const compatibleConnections = getCompatibleConnections(selectedDevice, mode);
        const connectionType = compatibleConnections.some(connection => (
            connection.id === this.state.connectionType
        )) ? this.state.connectionType : getDefaultConnectionType(selectedDevice, mode);
        this.setState({connectionType, mode, statusMessage: ''});
    }
    handleSelectDevice (event) {
        const selectedDeviceId = event.currentTarget.value;
        const selectedDevice = getHardwareDevice(selectedDeviceId);
        const savedAddress = loadSavedAddress(selectedDeviceId);
        this.setState({
            address: savedAddress || getDefaultAddress(selectedDeviceId),
            connected: this.isDeviceConnected(selectedDeviceId),
            connectionType: getDefaultConnectionType(selectedDevice, 'online'),
            deviceInfo: null,
            hasSavedAddress: Boolean(savedAddress),
            mode: 'online',
            selectedDeviceId,
            statusMessage: ''
        }, this.handleAutoConnect);
    }
    async connectCar (automatic) {
        const connectCar = this.props.vm.runtime.getOpcodeFunction('domiCar_connect');
        if (!connectCar) {
            if (this._isMounted) {
                this.setState({statusMessage: this.props.intl.formatMessage(messages.connectUnavailable)});
            }
            return false;
        }

        this.setState({connecting: true, statusMessage: ''});
        try {
            const connected = await connectCar({ADDRESS: this.state.address}, null);
            if (!this._isMounted) return Boolean(connected);
            this.setState({
                connected: Boolean(connected),
                connecting: false,
                hasSavedAddress: Boolean(connected) || this.state.hasSavedAddress,
                statusMessage: connected ? (automatic ?
                    this.props.intl.formatMessage(messages.autoReconnectSuccess) : '') : (automatic ?
                    this.props.intl.formatMessage(messages.autoReconnectFailed) :
                    this.props.intl.formatMessage(messages.notReachable))
            });
            return Boolean(connected);
        } catch (error) {
            if (this._isMounted) {
                this.setState({
                    connected: false,
                    connecting: false,
                    deviceInfo: null,
                    statusMessage: automatic ? this.props.intl.formatMessage(messages.autoReconnectFailed) :
                        this.props.intl.formatMessage(messages.connectFailed, {message: error.message})
                });
            }
            return false;
        }
    }
    async connectRound (automatic, connectionType) {
        const opcode = connectionType === 'usb' ? 'domiAiRound_connectUsb' : 'domiAiRound_connectWifi';
        const connectRound = this.props.vm.runtime.getOpcodeFunction(opcode);
        if (!connectRound) {
            if (this._isMounted) {
                this.setState({statusMessage: this.props.intl.formatMessage(messages.roundUnavailable)});
            }
            return false;
        }

        this.setState({connecting: true, statusMessage: ''});
        try {
            const args = connectionType === 'wifi' ? {ADDRESS: this.state.address} : {};
            const connected = await connectRound(args, null);
            if (!this._isMounted) return Boolean(connected);
            this.setState({
                connected: Boolean(connected),
                connecting: false,
                hasSavedAddress: connectionType === 'wifi' && connected ? true : this.state.hasSavedAddress,
                statusMessage: connected ? (automatic ?
                    this.props.intl.formatMessage(messages.roundAutoReconnectSuccess) : '') : (automatic ?
                    this.props.intl.formatMessage(messages.roundAutoReconnectFailed) :
                    this.props.intl.formatMessage(messages.roundNotReachable))
            });
            return Boolean(connected);
        } catch (error) {
            if (this._isMounted) {
                this.setState({
                    connected: false,
                    connecting: false,
                    deviceInfo: null,
                    statusMessage: automatic ? this.props.intl.formatMessage(messages.roundAutoReconnectFailed) :
                        this.props.intl.formatMessage(messages.connectFailed, {message: error.message})
                });
            }
            return false;
        }
    }
    async handlePrimaryAction () {
        const {connectionType, mode, selectedDeviceId} = this.state;
        if (selectedDeviceId === 'domiCar' && connectionType === 'serial') {
            this.props.onOpenDomiDeviceCenter();
            return;
        }
        if (selectedDeviceId === 'microbit' && mode === 'online') {
            this.props.onOpenConnectionModal('microbit');
            return;
        }
        if (selectedDeviceId === 'microbit' && mode === 'upload') {
            this.props.vm.emit(OPEN_BURN_CENTER_EVENT, 'microbit');
            return;
        }
        if (selectedDeviceId === 'domiAiRound') {
            await this.connectRound(false, connectionType);
            return;
        }
        if (selectedDeviceId !== 'domiCar' || connectionType !== 'wifi') return;

        const connected = await this.connectCar(false);
        if (connected && mode === 'upload') {
            this.props.vm.emit(OPEN_BURN_CENTER_EVENT, 'domiCar');
        }
    }
    render () {
        return (
            <HardwareDevicePaneComponent
                {...this.state}
                onAddDevice={this.props.onAddDevice}
                onAddressChange={this.handleAddressChange}
                onConnectionTypeChange={this.handleConnectionTypeChange}
                onModeChange={this.handleModeChange}
                onPrimaryAction={this.handlePrimaryAction}
                onSelectDevice={this.handleSelectDevice}
            />
        );
    }
}

HardwareDevicePane.propTypes = {
    intl: intlShape.isRequired,
    vm: PropTypes.instanceOf(VM).isRequired,
    onAddDevice: PropTypes.func.isRequired,
    onOpenConnectionModal: PropTypes.func.isRequired,
    onOpenDomiDeviceCenter: PropTypes.func.isRequired
};

const mapDispatchToProps = dispatch => ({
    onAddDevice: () => dispatch(openExtensionLibrary()),
    onOpenConnectionModal: extensionId => {
        dispatch(setConnectionModalExtensionId(extensionId));
        dispatch(openConnectionModal());
    },
    onOpenDomiDeviceCenter: () => dispatch(openDomiDeviceCenter())
});

export {HardwareDevicePane};
export default injectIntl(connect(null, mapDispatchToProps)(HardwareDevicePane));
